import type { GridColDef, GridRowParams, GridRowScrollEndParams, GridSortModel } from '@mui/x-data-grid-pro';
import type { SnackBarConfig } from '@shared/components/Snackbar';
import type { Company } from '@/scenes/authentication/types/types.auth';
import { AddCircle, CheckCircle, Edit } from '@mui/icons-material';
import { Box, Button, Chip, IconButton, TextField, Tooltip } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import axios from 'axios';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';
import CompanyLogo from '@shared/components/CompanyLogo';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import CustomSnackbar from '@shared/components/Snackbar';
import { UserContext } from '@/shared/contexts/UserContext';
import { usePrefetchLogos } from '@shared/hooks/usePrefetchLogos';
import { useApi } from '@shared/services/api';
import { Colors, H4 } from '@shared/styles';
import { StyledAutocomplete, TableHeaderWrapper } from '../styles/admin.styles';
import AdminEditCompany from './AdminEditCompany';
import BulkCompanyCreationModal from './BulkCompanyCreationModal';
import { log } from '../../utils/log';

interface AdminWaitlistTableProps {
  tabChange?: string;
}

// Company data interface for DataGrid rows
interface CompanyTableRow {
  id: number;
  logo: string | undefined;
  companyName: string;
  stockTicker: string;
  cik: string;
  gicsCode: string;
  reviewCount: number;
  createdAt: string;
  isApproved: boolean;
  rawCompany: Company;
}

const NoResultWrapper = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const StyledNoResultHeader = styled(H4)`
  margin: var(--spacing-md) auto;
  align-items: center;
  text-align: center;
`;

export const CompanyRequestsTable: React.FC<AdminWaitlistTableProps> = () => {
  const { user, isLoggedIn } = useContext(UserContext);
  const api = useApi();

  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [rows, setRows] = useState<CompanyTableRow[]>([]);
  const [searchInput, setSearchInput] = useState<string>('');
  const [debouncedSearchTerm] = useDebounce(searchInput, 300);
  const [snackbar, setSnackbar] = useState<SnackBarConfig | undefined>();
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'companyName', sort: 'asc' }]);

  // Derive orderBy and orderDirection from sortModel
  const orderBy = sortModel[0]?.field || 'companyName';
  const orderDirection = (sortModel[0]?.sort || 'asc').toUpperCase();

  // Infinite scrolling states
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextPageCompanies, setNextPageCompanies] = useState<Company[]>([]);
  const PAGE_SIZE = 100;

  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [bulkModalOpen, setBulkModalOpen] = useState<boolean>(false);

  // Prefetch logos for better UX
  usePrefetchLogos(
    rows.map((r) => r.rawCompany),
    {
      additionalCompanies: nextPageCompanies,
      maxPrefetch: 80,
      delay: 100,
      enabled: !initialLoading,
    },
  );

  // Fetch initial data and handle search
  useEffect(() => {
    const fetchData = async () => {
      if (!isLoggedIn) return;

      // Use isSearching for subsequent searches, initialLoading only for first load
      const isInitialLoad = rows.length === 0 && !debouncedSearchTerm;
      if (isInitialLoad) {
        setInitialLoading(true);
      } else {
        setIsSearching(true);
      }

      setCurrentOffset(0);
      setHasNextPage(true);
      setNextPageCompanies([]);
      try {
        const params: any = {
          approvedOnly: false,
          limit: PAGE_SIZE,
          offset: 0,
          orderBy,
          orderDirection,
        };

        // Add search term if provided
        if (debouncedSearchTerm.trim() !== '') {
          params.searchTerm = debouncedSearchTerm.trim();
        }

        const response = await api.get('/company', { params });
        if (!response || !response.results) {
          throw new Error('Failed to fetch company requests');
        }

        const companies: Company[] = response.results;

        const tableRows: CompanyTableRow[] = companies.map((c) => ({
          id: c.id ?? 0,
          logo: c.imageRef,
          companyName: c.companyName ?? 'Unknown Company',
          stockTicker: c.stockTicker ?? 'N/A',
          cik: c.cik ?? 'N/A',
          gicsCode: c.gicsCode ?? 'N/A',
          reviewCount: c.reviewCount ?? 0,
          followerCount: c.followerCount ?? 0,
          createdAt: dayjs(c.createdAt).format('MM/DD/YYYY'),
          isApproved: c.isApproved ?? false,
          rawCompany: c,
        }));

        setRows(tableRows);
        setCurrentOffset(0);
        setHasNextPage(response.meta?.hasMore ?? companies.length === PAGE_SIZE);
      } catch (err) {
        log.error('Error fetching company requests:', err);
        setSnackbar({
          message: 'An error has occurred, please try again later',
          type: 'error',
          open: true,
        });
      } finally {
        setInitialLoading(false);
        setIsSearching(false);
      }
    };

    fetchData();
  }, [user?.idToken, orderBy, orderDirection, debouncedSearchTerm]);

  // Refetch data function for use after actions
  const refetchData = useCallback(async () => {
    if (!isLoggedIn) return;
    setIsSearching(true);
    setCurrentOffset(0);
    setHasNextPage(true);
    setNextPageCompanies([]);
    try {
      const params: any = {
        approvedOnly: false,
        limit: PAGE_SIZE,
        offset: 0,
        orderBy,
        orderDirection,
      };

      // Add search term if provided
      if (debouncedSearchTerm.trim() !== '') {
        params.searchTerm = debouncedSearchTerm.trim();
      }

      const response = await api.get('/company', { params });
      if (!response || !response.results) {
        throw new Error('Failed to fetch company requests');
      }

      const companies: Company[] = response.results;

      const tableRows: CompanyTableRow[] = companies.map((c) => ({
        id: c.id ?? 0,
        logo: c.imageRef,
        companyName: c.companyName ?? 'Unknown Company',
        stockTicker: c.stockTicker ?? 'N/A',
        cik: c.cik ?? 'N/A',
        gicsCode: c.gicsCode ?? 'N/A',
        reviewCount: c.reviewCount ?? 0,
        createdAt: dayjs(c.createdAt).format('MM/DD/YYYY'),
        isApproved: c.isApproved ?? false,
        rawCompany: c,
      }));

      setRows(tableRows);
      setHasNextPage(response.meta?.hasMore ?? companies.length === PAGE_SIZE);
    } catch (err) {
      log.error('Error fetching company requests:', err);
      setSnackbar({
        message: 'An error has occurred, please try again later',
        type: 'error',
        open: true,
      });
    } finally {
      setIsSearching(false);
    }
  }, [user?.idToken, orderBy, orderDirection, debouncedSearchTerm]);

  // Load more companies for infinite scroll
  const loadMoreCompanies = useCallback(async () => {
    if (!hasNextPage || isLoadingMore || !isLoggedIn) {
      return;
    }

    setIsLoadingMore(true);
    try {
      const nextOffset = currentOffset + PAGE_SIZE;

      const params: any = {
        approvedOnly: false,
        limit: PAGE_SIZE,
        offset: nextOffset,
        orderBy,
        orderDirection,
      };

      // Add search term if provided
      if (debouncedSearchTerm.trim() !== '') {
        params.searchTerm = debouncedSearchTerm.trim();
      }

      const response = await api.get('/company', { params });

      if (!response || !response.results) {
        throw new Error('Failed to fetch more companies');
      }

      const companies: Company[] = response.results;
      const tableRows: CompanyTableRow[] = companies.map((c) => ({
        id: c.id ?? 0,
        logo: c.imageRef,
        companyName: c.companyName ?? 'Unknown Company',
        stockTicker: c.stockTicker ?? 'N/A',
        cik: c.cik ?? 'N/A',
        gicsCode: c.gicsCode ?? 'N/A',
        reviewCount: c.reviewCount ?? 0,
        createdAt: dayjs(c.createdAt).format('MM/DD/YYYY'),
        isApproved: c.isApproved ?? false,
        rawCompany: c,
      }));

      // Append new rows, deduplicating by ID
      setRows((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const uniqueNewRows = tableRows.filter((r) => !existingIds.has(r.id));
        return [...prev, ...uniqueNewRows];
      });
      setCurrentOffset(nextOffset);
      setHasNextPage(response.meta?.hasMore ?? companies.length === PAGE_SIZE);
    } catch (error) {
      log.error('Error loading more companies:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasNextPage,
    isLoadingMore,
    currentOffset,
    PAGE_SIZE,
    user?.idToken,
    api,
    orderBy,
    orderDirection,
    debouncedSearchTerm,
  ]);

  // Predictively load next page for prefetching
  const prefetchNextPage = useCallback(async () => {
    if (!hasNextPage || isLoadingMore || nextPageCompanies.length > 0 || !isLoggedIn) {
      return;
    }

    try {
      const nextOffset = currentOffset + PAGE_SIZE;

      const params: any = {
        approvedOnly: false,
        limit: PAGE_SIZE,
        offset: nextOffset,
        orderBy,
        orderDirection,
      };

      // Add search term if provided
      if (debouncedSearchTerm.trim() !== '') {
        params.searchTerm = debouncedSearchTerm.trim();
      }

      const response = await api.get('/company', { params });
      if (response?.results) {
        setNextPageCompanies(response.results);
      }
    } catch (error) {
      log.error('Error prefetching next page:', error);
    }
  }, [
    hasNextPage,
    isLoadingMore,
    nextPageCompanies.length,
    currentOffset,
    PAGE_SIZE,
    user?.idToken,
    api,
    orderBy,
    orderDirection,
    debouncedSearchTerm,
  ]);

  // Trigger predictive prefetching when rows load or offset changes
  useEffect(() => {
    if (rows.length >= PAGE_SIZE * 0.5 && hasNextPage && !isLoadingMore && nextPageCompanies.length === 0) {
      const timer = setTimeout(() => prefetchNextPage(), 500);
      return () => clearTimeout(timer);
    }
  }, [rows.length, currentOffset, PAGE_SIZE, hasNextPage, isLoadingMore, nextPageCompanies.length, prefetchNextPage]);

  // Reset pagination when sort changes
  useEffect(() => {
    setCurrentOffset(0);
    setHasNextPage(true);
    setNextPageCompanies([]);
  }, [orderBy, orderDirection]);

  // Handle sort model change
  const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  }, []);

  // Handle infinite scroll
  const handleRowScrollEnd = useCallback(
    async (_params: GridRowScrollEndParams) => {
      if (!hasNextPage || isLoadingMore || isSearching) {
        return;
      }

      setIsLoadingMore(true);

      try {
        if (nextPageCompanies.length > 0) {
          const tableRows: CompanyTableRow[] = nextPageCompanies.map((c) => ({
            id: c.id ?? 0,
            logo: c.imageRef,
            companyName: c.companyName ?? 'Unknown Company',
            stockTicker: c.stockTicker ?? 'N/A',
            cik: c.cik ?? 'N/A',
            gicsCode: c.gicsCode ?? 'N/A',
            reviewCount: c.reviewCount ?? 0,
            createdAt: dayjs(c.createdAt).format('MM/DD/YYYY'),
            isApproved: c.isApproved ?? false,
            rawCompany: c,
          }));

          // Check for duplicates before appending - use company IDs for deduplication
          setRows((prev) => {
            const existingIds = new Set(prev.map((r) => r.id));
            const uniqueNewRows = tableRows.filter((r) => !existingIds.has(r.id));
            return [...prev, ...uniqueNewRows];
          });
          setCurrentOffset((prev) => prev + PAGE_SIZE);
          setNextPageCompanies([]);
          // Note: Don't call prefetchNextPage here - let the useEffect handle it
          // to avoid stale closure issues with currentOffset
        } else {
          await loadMoreCompanies();
        }
      } finally {
        setIsLoadingMore(false);
      }
    },
    [hasNextPage, isLoadingMore, isSearching, nextPageCompanies, loadMoreCompanies, PAGE_SIZE],
  );

  // Handle approve click - directly POST to approve endpoint
  const handleApproveClick = async (companyName: string, companyId: number) => {
    if (!isLoggedIn) return;
    setIsSearching(true);
    try {
      const result = await api.post(`/admin/company/${companyId}/approve`);

      if (result && typeof result === 'object' && 'success' in result && !result.success) {
        throw new Error(result.error.message);
      }

      // Update only the specific row instead of refetching all data
      setRows((prevRows) => prevRows.map((row) => (row.id === companyId ? { ...row, isApproved: true } : row)));

      setSnackbar({
        message: `${companyName} has been approved successfully!`,
        type: 'success',
        open: true,
      });
    } catch {
      setSnackbar({
        message: 'An error has occurred, please try again later',
        type: 'error',
        open: true,
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Handle Edit/Add Company
  const handleEdit = async (updatedCompany: Partial<Company>) => {
    if (!isLoggedIn || !selectedCompany) return;
    setIsSearching(true);
    try {
      const isNewCompany = !selectedCompany.id;
      const url = isNewCompany ? '/admin/company' : `/admin/company/${selectedCompany.id}`;
      const response = await axios.put(`${import.meta.env.VITE_API_BASE_URL}${url}`, updatedCompany, {
        headers: {
          Authorization: user?.idToken,
        },
      });

      if (response.status !== 200) {
        throw new Error('Failed to save company');
      }

      setEditModalOpen(false);
      setSelectedCompany(null);

      // Update the specific row or add new row instead of refetching all data
      if (isNewCompany) {
        // For new companies, we should still refetch since we need the full company data
        await refetchData();
      } else {
        // For updates, just update the specific row
        setRows((prevRows) =>
          prevRows.map((row) =>
            row.id === selectedCompany.id
              ? {
                  ...row,
                  companyName: updatedCompany.companyName ?? row.companyName,
                  stockTicker: updatedCompany.stockTicker ?? row.stockTicker,
                  cik: updatedCompany.cik ?? row.cik,
                  gicsCode: updatedCompany.gicsCode ?? row.gicsCode,
                  isApproved: updatedCompany.isApproved ?? row.isApproved,
                  rawCompany: { ...row.rawCompany, ...updatedCompany },
                }
              : row,
          ),
        );
      }

      setSnackbar({
        message: `${updatedCompany.companyName} has been ${isNewCompany ? 'created' : 'updated'} successfully!`,
        type: 'success',
        open: true,
      });
    } catch (error) {
      log.error('Error saving company:', error);
      setSnackbar({
        message: 'An error has occurred, please try again later',
        type: 'error',
        open: true,
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Handle company selection from autocomplete
  const handleCompanySelect = async (companyName: string) => {
    // Search in currently loaded rows first
    const selectedRow = rows.find((r) => r.companyName === companyName);
    if (selectedRow) {
      try {
        const data = await api.get<Company>(`/admin/company/${selectedRow.id}`);
        if (data && typeof data === 'object' && 'success' in data && !data.success) {
          setSelectedCompany(data as Company);
        }

        setSelectedCompany(data as Company);
        setEditModalOpen(true);
      } catch {
        setSnackbar({
          message: 'Failed to load company data',
          type: 'error',
          open: true,
        });
      }
    }
  };

  // Handle adding a new company
  const handleAddNewCompany = () => {
    const newCompany: Company = {
      companyName: searchInput || '',
      isApproved: false,
    };
    setSelectedCompany(newCompany);
    setEditModalOpen(true);
  };

  // Use rows directly - filtering is done server-side

  // Handle edit click
  const handleEditClick = async (companyRow: CompanyTableRow) => {
    try {
      const data = await api.get<Company>(`/admin/company/${companyRow.id}`);
      setSelectedCompany(data as Company);
      setEditModalOpen(true);
    } catch {
      setSnackbar({
        message: 'Failed to load company data',
        type: 'error',
        open: true,
      });
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      sortable: true,
    },
    {
      field: 'logo',
      headerName: 'Logo',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Link
          to={`/company/${encodeURIComponent(params.row.companyName!)}`}
          style={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          <CompanyLogo
            companyId={params.row.id}
            companyName={params.row.companyName}
            companyUrl={params.row.rawCompany.url}
            sx={{ width: 40, height: 40 }}
            lazy
          />
        </Link>
      ),
    },
    {
      field: 'companyName',
      headerName: 'Company Name',
      width: 200,
      flex: 1,
      sortable: true,
    },
    {
      field: 'stockTicker',
      headerName: 'Stock Ticker',
      width: 120,
      sortable: true,
    },
    {
      field: 'cik',
      headerName: 'CIK',
      width: 120,
      sortable: true,
    },
    {
      field: 'gicsCode',
      headerName: 'GICS Code',
      width: 120,
      sortable: false,
    },
    {
      field: 'reviewCount',
      headerName: 'Reviews',
      width: 100,
      sortable: true,
      type: 'number',
    },
    {
      field: 'followerCount',
      headerName: 'Followers',
      width: 100,
      sortable: true,
      type: 'number',
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      sortable: true,
    },
    {
      field: 'isApproved',
      headerName: 'Status',
      width: 100,
      sortable: true,
      renderCell: (params) => (
        <Chip label={params.value ? 'Approved' : 'Pending'} color={params.value ? 'success' : 'warning'} size="small" />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit Company">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(params.row);
              }}
              sx={{ mr: 1 }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          {!params.row.isApproved && (
            <Tooltip title="Approve Company">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleApproveClick(params.row.companyName, params.row.id);
                }}
                color="success"
              >
                <CheckCircle />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const buttonStyle = {
    backgroundColor: Colors.brandPrimary,
    borderRadius: '8px',
    color: Colors.white,
    fontSize: '17px',
    fontWeight: 600,
    height: '42px',
    margin: '10px 0 10px auto',
    overflow: 'hidden',
    textAlign: 'center',
    textTransform: 'none',
    transition: 'background-color 0.3s ease-in-out',
    '&:hover': {
      boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.3)',
      transform: 'translateY(-2px)',
    },
  };

  if (initialLoading) {
    return <LoadingSpinner variant="linear" size="medium" />;
  }

  return (
    <>
      <TableHeaderWrapper>
        <StyledAutocomplete
          freeSolo
          disableClearable
          options={rows.map((r) => ({
            label: r.companyName,
            value: r.companyName,
          }))}
          onChange={(event, value) => {
            if (value && typeof value === 'object' && 'value' in value && typeof value.value === 'string') {
              handleCompanySelect(value.value);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search by Company name"
              type="search"
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={params.InputProps as any}
            />
          )}
        />
        <Button onClick={() => setBulkModalOpen(true)} sx={buttonStyle} startIcon={<AddCircle />}>
          Bulk Add Companies
        </Button>
      </TableHeaderWrapper>

      {rows.length > 0 ? (
        <Box sx={{ height: 'calc(100vh - 300px)', width: '100%', mt: 2 }}>
          <DataGridPro
            rows={rows}
            columns={columns}
            loading={isSearching || isLoadingMore}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            onRowClick={(params: GridRowParams) => handleEditClick(params.row)}
            onRowsScrollEnd={handleRowScrollEnd}
            scrollEndThreshold={600}
            pagination={false}
            disableRowSelectionOnClick
            sx={{
              boxShadow: 2,
              border: 2,
              borderColor: 'primary.light',
              height: '100%',
              width: '100%',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                color: '#1976d2',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                borderBottom: '2px solid #1976d2',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold',
                },
              },
              '& .MuiDataGrid-columnHeader:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-columnHeader:focus-within': {
                outline: 'none',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '& .MuiDataGrid-columnSeparator': {
                display: 'none',
              },
              '& .MuiDataGrid-footerContainer': {
                display: 'none', // Hide footer since we don't need pagination controls
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                margin: 0,
              },
            }}
          />
        </Box>
      ) : (
        <NoResultWrapper>
          <StyledNoResultHeader>No companies found matching your search.</StyledNoResultHeader>
          <Button data-testid="admin-add-company-button" onClick={() => handleAddNewCompany()} sx={buttonStyle}>
            ➕ Add New Company
          </Button>
        </NoResultWrapper>
      )}

      <AdminEditCompany
        isOpen={editModalOpen}
        setIsOpen={() => {
          setEditModalOpen(false);
          setSelectedCompany(null);
        }}
        company={selectedCompany}
        onSave={handleEdit}
      />

      <BulkCompanyCreationModal
        isOpen={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        onComplete={() => {
          setBulkModalOpen(false);
          refetchData();
        }}
      />

      <CustomSnackbar config={snackbar} setOpen={(open) => setSnackbar((cfg) => (cfg ? { ...cfg, open } : cfg))} />
    </>
  );
};

export default CompanyRequestsTable;
